import { buildClientMetadata } from './clientMetadata.js';
import type { ShellHydration } from './hydration.js';

export interface EventIdentity {
  // Exactly one of projectUserId or anonymousId is populated. sessionId is
  // always sent (per-tab dedup at the AnnouncementEvent / SurveyEvent level).
  projectUserId?: string;
  anonymousId?: string;
  sessionId?: string;
}

// Pick a single canonical identifier per event:
//   * If the user identified themselves via Chirp.identify({ email }), the
//     projectUser row is the durable record — use its id and OMIT anonymousId.
//   * Otherwise fall back to the browser-scoped anonymousId.
// This keeps event rows clean (no double-attribution) and matches the
// server-side Phase 3 dismiss filter, which queries either field via $or.
export function resolveEventIdentity(hydration: ShellHydration): EventIdentity {
  const sessionId = hydration.sessionId;
  if (hydration.projectUserId) {
    return { projectUserId: hydration.projectUserId, sessionId };
  }
  if (hydration.anonymousId) {
    return { anonymousId: hydration.anonymousId, sessionId };
  }
  return { sessionId };
}

export interface EventMeta {
  url?: string;
  referrer?: string;
  userAgent?: string;
  browser?: string;
  os?: string;
  device?: string;
  [extra: string]: unknown;
}

// Build the meta blob for an event. Only includes fields that fit the strict
// AnnouncementEventMetaInput; callers with JSON meta (survey / widget feedback)
// layer richer data on top via buildClientMetadata().
export function buildEventMeta(
  hydration: ShellHydration,
  extra?: Record<string, unknown>,
): EventMeta {
  const client = buildClientMetadata(hydration.host?.url);
  return {
    url: hydration.host?.url,
    referrer: hydration.host?.referrer,
    userAgent: client.userAgent,
    browser: client.browser,
    os: client.os,
    device: client.device,
    ...extra,
  };
}
